import { useTRPC } from "@/lib/trpc";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

interface UseMediaUploadOptions {
  // biome-ignore lint/suspicious/noExplicitAny: Media type from TRPC is complex
  onSuccess?: (media: any) => void;
  onError?: (error: Error) => void;
}

export function useMediaUpload({
  onSuccess,
  onError,
}: UseMediaUploadOptions = {}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const trpc = useTRPC();

  const presignMutation = useMutation(trpc.media.presign.mutationOptions());
  const confirmMutation = useMutation(trpc.media.confirm.mutationOptions());

  const uploadFile = async (
    file: File,
    metadata: {
      title: string;
      description: string;
    },
  ) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get presigned URL
      const presignResult = await presignMutation.mutateAsync({
        contentType: file.type,
        extension: file.name.split(".").pop() || "",
        mediaType: "organization",
        isPrivate: false,
      });

      if (!presignResult.success || !presignResult.presignedUrl) {
        throw new Error("Failed to get presigned URL");
      }

      // Step 2: Upload file to presigned URL
      setUploadProgress(25);
      const uploadResponse = await fetch(presignResult.presignedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file");
      }

      setUploadProgress(75);

      // Step 3: Confirm upload and create media record
      const confirmResult = await confirmMutation.mutateAsync({
        cloudStorageObjectId: presignResult.cloudStorageObjectId,
        title: metadata.title,
        description: metadata.description,
      });

      setUploadProgress(100);

      if (confirmResult.success && onSuccess) {
        onSuccess(confirmResult.media);
      }

      return confirmResult.media;
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error("Upload failed");
      if (onError) {
        onError(errorObj);
      }
      throw errorObj;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return {
    uploadFile,
    isUploading,
    uploadProgress,
    presignMutation,
    confirmMutation,
  };
}
